import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { category, categoryArr, FallaufgabeAdmin } from '@reflact/ipkv-types';
import { ConfirmationService, MenuItem, MessageService, TreeNode } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { DialogModule } from "primeng/dialog";
import { FloatLabel } from 'primeng/floatlabel';
import { InplaceModule } from 'primeng/inplace';
import { InputTextModule } from 'primeng/inputtext';
import { MenubarModule } from 'primeng/menubar';
import { Message } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TreeTableModule } from "primeng/treetable";
import { FallaufgabenService } from '../../services/fallaufgaben-service';

@Component({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    TableModule,
    InplaceModule,
    Message,
    ConfirmDialog,
    InputTextModule,
    TagModule,
    TreeTableModule,
    ToggleSwitchModule,
    DialogModule,
    SelectModule,
    SelectButtonModule,
    FloatLabel,
    MenubarModule,
    BreadcrumbModule,
    ContextMenuModule,
    DialogModule
  ],
  templateUrl: './fallaufgaben.html',
  styleUrl: './fallaufgaben.scss',
  providers: [ConfirmationService]
})
export class Fallaufgaben implements OnInit {
  @ViewChild('cm') cm!: ContextMenu;

  protected fallaufgabenService = inject(FallaufgabenService);
  private messageService = inject(MessageService);
  private confirmService = inject(ConfirmationService);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  protected home: MenuItem = { label: 'Fallaufgaben' };
  protected menuItems: MenuItem[] = [
    {
      command: () => {
        this.newFallaufgabeFormVisible = true;
      },
      label: "Fallaufgabe erstellen"
    }
  ];

  protected filterMode: FallaufgabeAdmin["state"][] = ["draft", "live"];
  protected filterOptions: { name: string, value: FallaufgabeAdmin["state"] }[] = [
    { name: 'Draft', value: "draft" },
    { name: 'Live', value: "live" },
    { name: 'Archiviert', value: "archived" },
  ]
  protected selectedCategory: category | null = null;
  protected selectedType: FallaufgabeAdmin['type'] | null = null;

  protected newFallaufgabenForm = this.fb.group({
    name: ["", [Validators.required]]
  });
  protected newFallaufgabeFormSubmitted: boolean = false;
  protected newFallaufgabeFormVisible: boolean = false;
  protected newFallaufgabeFormCategory: category = 'Abrechnungsmanager/-in Arztpraxis';
  protected newFallaufgabeFormType: FallaufgabeAdmin['type'] = 'exam';
  protected tasks: TreeNode[] = [];
  protected isCopyVisible: boolean = false;
  protected isCreateConfirmVisible: boolean = false;
  protected isDublicateVisible: boolean = false;
  protected copyInfo: { _id: string, name: string } = { _id: '', name: '' };
  protected categories = [...categoryArr];
  protected types: { value: FallaufgabeAdmin['type'], label: string }[] = [{ value: 'exam', label: 'Exam' }, { value: 'vc', label: 'VC' }];

  public ngOnInit(): void {
    this.createTreeNode();
  }

  protected toggleParentNode(rowNode: any) {
    rowNode.node.expanded = !rowNode.node.expanded;
    this.tasks = [...this.tasks];
  }

  protected createTreeNode(expandedParent: string = '') {
    this.tasks = [];
    this.fallaufgabenService.fallaufgaben.forEach((fa) => {
      if (this.filterMode.includes(fa.state)) {
        this.subprocessTreeNode(fa, expandedParent);
      }
    });
  }

  private subprocessTreeNode(fa: FallaufgabeAdmin, expandedParent: string = '') {
    const parentIndex = this.tasks.findIndex((t) => t.data.id === fa.versionsUnique);
    if (parentIndex !== -1 && this.tasks[parentIndex]) {
      this.tasks[parentIndex].children?.push({
        key: fa._id,
        data: {
          name: 'Version ' + fa.version,
          faId: fa._id,
          version: fa.version,
          state: fa.state,
          type: fa.type,
          isParent: false
        }
      });
      if (this.tasks[parentIndex].key === expandedParent) {
        this.tasks[parentIndex].expanded = true;
      }
      this.tasks[parentIndex].children?.sort((a, b) => {
        if (a.data.version > b.data.version) return -1;
        if (a.data.version < b.data.version) return 1;
        return 0;
      });
    } else {

      const liveFA = this.fallaufgabenService.fallaufgaben.find(f => f.versionsUnique == fa.versionsUnique && f.state == "live")

      const newTreeNode: TreeNode = {
        key: fa.versionsUnique,
        data: {
          id: fa.versionsUnique,
          name: liveFA == null ? fa.name : liveFA.name,
          category: fa.category,
          type: fa.type,
          isParent: true
        },
        children: [
          {
            key: fa._id,
            data: {
              name: 'Version ' + fa.version,
              faId: fa._id,
              version: fa.version,
              state: fa.state,
              type: fa.type,
              isParent: false
            }
          }
        ]
      }
      if (newTreeNode.key === expandedParent) {
        newTreeNode.expanded = true;
      }
      this.tasks.push(newTreeNode)
    }
  }

  protected async createFallaufgabe() {
    this.newFallaufgabeFormSubmitted = true;
    if (this.newFallaufgabenForm.valid) {
      this.newFallaufgabeFormVisible = false;
      this.isCreateConfirmVisible = true;
    }
  }

  protected async confirmedCreate() {
    const id = await this.fallaufgabenService.create(this.newFallaufgabenForm.get("name")?.value!, this.newFallaufgabeFormCategory, this.newFallaufgabeFormType);
    this.isCreateConfirmVisible = false;
    this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `Fallaufgabe wurde angelegt.`, life: 3000 });
    this.navigateTo(id);
  }

  protected isInvalid(controlName: string) {
    const control = this.newFallaufgabenForm.get(controlName);
    return control?.invalid && (control.touched || this.newFallaufgabeFormSubmitted);
  }

  protected async confirmDelete() {
    if (this.fallaufgabenService.selectedFallaufgabe.state !== 'archived') {
      this.confirmService.confirm({
        position: 'top',
        message: `Sind Sie sicher, dass Sie <b>${this.fallaufgabenService.selectedFallaufgabe.name}</b> löschen möchten?`,
        icon: 'pi pi-exclamation-triangle',
        rejectButtonProps: {
          label: 'Abbrechen',
          severity: 'secondary',
          outlined: true
        },
        acceptButtonProps: {
          label: 'Löschen',
          severity: 'danger',
        },
        accept: async () => {
          await this.fallaufgabenService.delete(this.fallaufgabenService.selectedFallaufgabe._id ?? '');
          this.messageService.add({ severity: 'success', summary: 'Erfolgreich', detail: `${this.fallaufgabenService.selectedFallaufgabe.name} wurde gelöscht.`, life: 3000 });
          this.createTreeNode(this.fallaufgabenService.selectedFallaufgabe.versionsUnique);
        }
      });
    }
  }

  protected navigateTo(id: string = '') {
    if (id !== '') {
      void this.router.navigate(["/", "fallaufgaben", id]);
    } else if (this.fallaufgabenService.selectedFallaufgabe.state === 'live') {
      this.isDublicateVisible = true;
    } else {
      void this.router.navigate(["/", "fallaufgaben", this.fallaufgabenService.selectedFallaufgabe._id]);
    }
  }

  protected async createDublicate() {
    await this.fallaufgabenService.createDuplicate(this.fallaufgabenService.selectedFallaufgabe._id);
    this.createTreeNode(this.fallaufgabenService.selectedFallaufgabe.versionsUnique);
  }

  protected acceptedEdit() {
    void this.router.navigate(["/fallaufgaben", this.fallaufgabenService.selectedFallaufgabe._id]);
  }

  protected async confirmedCopy() {
    this.isCopyVisible = false;
    const id = await this.fallaufgabenService.copy(this.copyInfo);
    this.onClick(id);
  }

  protected onClick(id: string) {
    this.fallaufgabenService.set(id);
    this.navigateTo();
  }

  protected onContextMenu(event: any, id: string) {
    this.fallaufgabenService.set(id);
    this.cm.target = event.currentTarget;
    this.cm.model = this.getContextItems();
    this.cm.show(event);
  }

  protected getContextItems(): MenuItem[] {
    return [
      {
        label: 'Öffnen',
        icon: 'pi pi-search',
        command: () => { this.navigateTo(); },
        visible: this.fallaufgabenService.selectedFallaufgabe.state === 'archived'
      },
      {
        label: 'Bearbeiten',
        icon: 'pi pi-pencil',
        command: () => { this.navigateTo(); },
        visible: this.fallaufgabenService.selectedFallaufgabe.state !== 'archived'
      },
      {
        label: 'Version als separate Fallaufgabe anlegen',
        icon: 'pi pi-clone',
        command: () => {
          this.copyInfo._id = this.fallaufgabenService.selectedFallaufgabe._id;
          this.copyInfo.name = this.fallaufgabenService.selectedFallaufgabe.name;
          this.isCopyVisible = true;
        }
      },
      {
        label: 'Neue Version der Fallaufgabe anlegen',
        icon: 'pi pi-file-plus',
        command: () => { this.createDublicate(); },
        visible: this.fallaufgabenService.selectedFallaufgabe.state === 'archived'
      },
      {
        label: 'Löschen',
        icon: 'pi pi-trash',
        command: () => { this.confirmDelete(); },
        disabled: this.fallaufgabenService.selectedFallaufgabe.state === 'archived'
      }
    ];
  }
}
